import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Star, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

function Stars({ value, onChange, size = 22, testIdPrefix }) {
  const interactive = typeof onChange === "function";
  return (
    <div className="flex gap-1" role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onChange(n)}
          data-testid={testIdPrefix ? `${testIdPrefix}-${n}` : undefined}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          className={`transition-transform ${interactive ? "hover:scale-110 cursor-pointer" : "cursor-default"}`}
        >
          <Star
            style={{ width: size, height: size }}
            className={
              n <= value
                ? "fill-amber-400 text-amber-400"
                : "fill-transparent text-[#5C625E]/40"
            }
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

export default function ReviewsPage() {
  const { business } = useBusiness();
  const [list, setList] = useState([]);
  const [stats, setStats] = useState({ count: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    rating: 5,
    title: "",
    body: "",
  });
  const [submittedStatus, setSubmittedStatus] = useState(null);
  const [submittedShare, setSubmittedShare] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/reviews");
      setList(data.reviews || []);
      setStats({ count: data.count || 0, average: data.average || 0 });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const distribution = useMemo(() => {
    const buckets = [0, 0, 0, 0, 0];
    list.forEach((r) => {
      if (r.rating >= 1 && r.rating <= 5) buckets[r.rating - 1] += 1;
    });
    return buckets;
  }, [list]);

  async function submit(e) {
    e?.preventDefault?.();
    if (!form.customer_name.trim() || !form.customer_email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (!form.body.trim()) {
      toast.error("Tell us a little about your experience");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews", form);
      setSubmittedStatus(data.status);
      setSubmittedShare(data.share || null);
      setForm({
        customer_name: "",
        customer_email: "",
        rating: 5,
        title: "",
        body: "",
      });
      if (data.status === "published") load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  return (
    <div data-testid="reviews-page" className="bg-[#FDFBF7]">
      <PlatformHeader
        brand={{ name: "HustleReviews", to: "/reviews" }}
        showBookCta={false}
        showCart={false}
      />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-10">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#5A7247]">
          Word of mouth
        </p>
        <h1
          data-testid="reviews-heading"
          className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 text-[#121614]"
        >
          What guests are saying about{" "}
          <span className="italic text-[#C05746]">
            {business?.business_name || "us"}
          </span>
        </h1>
        <div className="mt-8 flex flex-wrap gap-8 items-end">
          <div>
            <p
              data-testid="reviews-average"
              className="font-serif-display text-6xl text-[#121614] leading-none"
            >
              {stats.average?.toFixed?.(1) ?? "0.0"}
            </p>
            <div className="mt-2">
              <Stars value={Math.round(stats.average || 0)} />
            </div>
            <p className="text-sm text-[#5C625E] mt-2">
              Based on{" "}
              <span data-testid="reviews-count" className="font-medium text-[#121614]">
                {stats.count}
              </span>{" "}
              published review{stats.count === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex-1 min-w-[260px] space-y-1">
            {[5, 4, 3, 2, 1].map((n) => {
              const total = list.length || 1;
              const pct = Math.round((distribution[n - 1] / total) * 100);
              return (
                <div key={n} className="flex items-center gap-3 text-sm">
                  <span className="w-6 text-right text-[#5C625E]">{n}★</span>
                  <div className="flex-1 h-2 rounded-full bg-[#E8E5DF] overflow-hidden">
                    <div
                      className="h-full bg-amber-400"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-10 text-[#5C625E] text-xs">
                    {distribution[n - 1]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="bg-white border border-[#E8E5DF] rounded-2xl p-6 md:p-8">
          <h2 className="font-serif-display text-2xl text-[#121614]">
            Share your experience
          </h2>
          <p className="text-sm text-[#5C625E] mt-1 flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-[#5A7247]" strokeWidth={1.5} />
            4★ and above publish instantly. Lower ratings are sent privately to
            the owner first.
          </p>

          {submittedStatus ? (
            <div
              data-testid="reviews-submission-confirmation"
              data-banner={submittedStatus === "published" ? "published" : "pending"}
              className={`mt-6 p-4 rounded-xl border ${
                submittedStatus === "published"
                  ? "bg-[#5A7247]/10 border-[#5A7247]/40 text-[#2C3A22]"
                  : "bg-amber-50 border-amber-200 text-amber-900"
              }`}
            >
              <span data-testid="review-form-banner">
                {submittedStatus === "published"
                  ? "Thank you — your review is live on the page."
                  : "Thanks for the feedback. We've sent it privately to the owner so they can follow up directly."}
              </span>
              {submittedStatus === "published" && submittedShare &&
                (submittedShare.google || submittedShare.facebook || submittedShare.instagram) && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="review-share-buttons">
                  <span className="text-sm mr-1">Share it further:</span>
                  {submittedShare.google && (
                    <a href={submittedShare.google} target="_blank" rel="noreferrer"
                      data-testid="review-share-google"
                      className="text-xs font-semibold rounded-full bg-white border border-[#5A7247]/40 px-3 py-1 hover:bg-[#5A7247]/10">
                      ★ Post on Google
                    </a>
                  )}
                  {submittedShare.facebook && (
                    <a href={submittedShare.facebook} target="_blank" rel="noreferrer"
                      data-testid="review-share-facebook"
                      className="text-xs font-semibold rounded-full bg-white border border-[#5A7247]/40 px-3 py-1 hover:bg-[#5A7247]/10">
                      Facebook
                    </a>
                  )}
                  {submittedShare.instagram && (
                    <a href={submittedShare.instagram} target="_blank" rel="noreferrer"
                      data-testid="review-share-instagram"
                      className="text-xs font-semibold rounded-full bg-white border border-[#5A7247]/40 px-3 py-1 hover:bg-[#5A7247]/10">
                      Instagram
                    </a>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={() => setSubmittedStatus(null)}
                className="ml-2 underline text-sm"
                data-testid="reviews-write-another"
              >
                Write another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Your name</Label>
                  <Input
                    data-testid="review-form-name"
                    className="mt-1 rounded-xl"
                    value={form.customer_name}
                    onChange={(e) => set("customer_name", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Email (kept private)</Label>
                  <Input
                    data-testid="review-form-email"
                    type="email"
                    className="mt-1 rounded-xl"
                    value={form.customer_email}
                    onChange={(e) => set("customer_email", e.target.value)}
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Rating</Label>
                <div className="mt-2">
                  <Stars
                    value={form.rating}
                    onChange={(v) => set("rating", v)}
                    testIdPrefix="review-form-rating"
                  />
                </div>
              </div>
              <div>
                <Label>Headline (optional)</Label>
                <Input
                  data-testid="review-form-title"
                  className="mt-1 rounded-xl"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="One-line summary"
                />
              </div>
              <div>
                <Label>What stood out?</Label>
                <Textarea
                  data-testid="review-form-body"
                  className="mt-1 rounded-xl"
                  rows={5}
                  value={form.body}
                  onChange={(e) => set("body", e.target.value)}
                  placeholder="Tell future guests about your experience…"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  data-testid="review-form-submit"
                  type="submit"
                  disabled={submitting}
                  className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-7"
                >
                  {submitting ? "Sending…" : "Post review"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="font-serif-display text-3xl text-[#121614] mb-6">
          Recent reviews
        </h2>
        {loading ? (
          <p data-testid="reviews-loading" className="text-[#5C625E]">
            Loading…
          </p>
        ) : list.length === 0 ? (
          <p
            data-testid="reviews-empty"
            className="text-[#5C625E] bg-white rounded-xl border border-[#E8E5DF] p-6"
          >
            No public reviews yet — be the first to leave one above.
          </p>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {list.map((r) => (
              <li
                key={r.id}
                data-testid={`review-card-${r.id}`}
                className="bg-white border border-[#E8E5DF] rounded-2xl p-5"
              >
                <div className="flex items-center justify-between">
                  <Stars value={r.rating} size={16} />
                  <span className="text-xs text-[#5C625E]">
                    {(r.created_at || "").slice(0, 10)}
                  </span>
                </div>
                {r.title && (
                  <p className="mt-3 font-medium text-[#121614]">{r.title}</p>
                )}
                <p className="mt-2 text-sm text-[#5C625E] leading-relaxed">
                  {r.body}
                </p>
                <p className="mt-4 text-xs uppercase tracking-wider text-[#5A7247]">
                  — {r.customer_name || "Anonymous"}
                </p>
                {(r.replies || []).length > 0 && (
                  <div className="mt-3 space-y-2 border-t border-[#F0EDE7] pt-3">
                    {r.replies.map((rep, i) => (
                      <div key={i} data-testid={`review-reply-${r.id}-${i}`}
                        className="rounded-lg bg-[#FBF7EE] border border-[#EFE9DC] p-3">
                        <p className="text-[11px] font-semibold text-[#C05746]">
                          Response from the business
                        </p>
                        <p className="text-sm text-[#3A3F3B] mt-0.5">{rep.body}</p>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      <SuiteFooter />
    </div>
  );
}
